import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, fmtRelative } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Sparkles,
  Send,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import AIAgentLoader from "../components/AIAgentLoader";

const STARTER_PROMPTS = [
  "What's the single biggest cost optimization I should fix today?",
  "Summarize the cost waste across our Vertex AI workloads.",
  "Which dev environments look abandoned and can I shut them down?",
  "Give me a 5-step plan to reduce BigQuery spend next quarter.",
];

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      data-testid={`chat-bubble-${role}`}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}
    >
      <div
        className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
          isUser ? "cs-chat-user" : "cs-chat-bot"
        }`}
      >
        {isUser ? (
          <span>{content}</span>
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ""}</ReactMarkdown>
        )}
      </div>
    </div>
  );
}

export default function AskDex() {
  const [enabled, setEnabled] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.dex.status().then((s) => setEnabled(!!s.enabled)).catch(() => setEnabled(false));
    refreshSessions();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const refreshSessions = async () => {
    try {
      const s = await api.dex.sessions();
      setSessions(s || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadSession = async (sid) => {
    setSessionId(sid);
    try {
      const hist = await api.dex.history(sid);
      setMessages(hist || []);
    } catch (e) {
      console.error(e);
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    try {
      await api.dex.deleteSession(sid);
      toast.success("Chat deleted");
      if (sid === sessionId) newChat();
      refreshSessions();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setSending(true);
    try {
      const res = await api.dex.chat(sessionId, msg);
      setSessionId(res.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: res.reply }]);
      refreshSessions();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `_Error reaching Dex:_ ${detail}` },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8" data-testid="ask-dex-page">
      <AIAgentLoader
        open={sending}
        title="Dex is thinking"
        subtitle="Grounding your question in the latest scan and crunching the right numbers."
      />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-180px)]">
        {/* Sessions panel */}
        <div className="cs-card p-3 flex flex-col">
          <Button
            onClick={newChat}
            data-testid="new-chat-button"
            className="bg-[#00579e] hover:bg-[#004580] text-white rounded-full mb-3"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New chat
          </Button>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground px-2 mb-1">
            Recent
          </div>
          <div className="flex-1 overflow-y-auto space-y-0.5">
            {sessions.length === 0 && (
              <div className="text-xs text-muted-foreground px-2 py-3">
                No conversations yet.
              </div>
            )}
            {sessions.map((s) => (
              <div
                key={s.session_id}
                onClick={() => loadSession(s.session_id)}
                data-testid={`session-${s.session_id}`}
                className={`group px-2.5 py-2 rounded-lg cursor-pointer flex items-start gap-2 text-xs ${
                  sessionId === s.session_id
                    ? "bg-[hsl(var(--cs-tint))] text-foreground"
                    : "text-muted-foreground hover:bg-[hsl(var(--cs-tint))] hover:text-foreground"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">
                    {s.preview || "(empty)"}
                  </div>
                  <div className="text-[10px] mt-0.5 text-muted-foreground">
                    {fmtRelative(s.last_ts)} · {s.msg_count} msg
                  </div>
                </div>
                <button
                  onClick={(e) => deleteSession(s.session_id, e)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-[#ef4444]" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Chat panel */}
        <div className="cs-card flex flex-col overflow-hidden">
          {/* Title */}
          <div className="px-6 py-4 border-b border-border flex items-center gap-3">
            <div className="w-9 h-9 rounded-full cs-gradient-bg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="cs-heading font-semibold text-base leading-none">
                Ask <span className="cs-gradient-text">Dex</span>
              </h2>
              <div className="text-[11px] text-muted-foreground mt-1">
                Your FinOps assistant, grounded in the latest scan
              </div>
            </div>
            {!enabled && (
              <div className="flex items-center gap-1.5 text-xs text-[#9a3412] bg-[#ffedd5] px-2.5 py-1 rounded-full">
                <AlertCircle className="w-3.5 h-3.5" />
                LLM key missing
              </div>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5">
            {messages.length === 0 && (
              <div className="max-w-xl mx-auto text-center py-8" data-testid="dex-empty-state">
                <div className="w-14 h-14 rounded-full cs-gradient-bg flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h3 className="cs-heading text-xl font-semibold tracking-tight">
                  Hi, I'm <span className="cs-gradient-text">Dex</span>.
                </h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Ask me anything about your GCP spend, your latest scan findings,
                  or how to fix specific cost issues. I'm grounded in your real
                  CloudScore data, not generic advice.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6 text-left">
                  {STARTER_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => send(p)}
                      data-testid={`starter-prompt-${i}`}
                      className="cs-card p-3 hover:border-[#00579e]/40 text-xs text-foreground leading-snug text-left transition-all"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <ChatBubble key={i} role={m.role} content={m.content} />
            ))}

            {sending && (
              <div className="flex justify-start mb-3" data-testid="dex-thinking">
                <div className="cs-chat-bot px-4 py-3 inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 cs-spin" />
                  Dex is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-border px-4 py-3 bg-white">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2"
            >
              <Input
                data-testid="dex-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Dex about cost waste, fixes, or trends…"
                disabled={!enabled || sending}
                className="rounded-full h-10 bg-[hsl(var(--cs-tint))] border-border focus-visible:ring-[#00579e]"
              />
              <Button
                type="submit"
                disabled={!enabled || sending || !input.trim()}
                data-testid="dex-send-button"
                className="bg-[#00579e] hover:bg-[#004580] text-white rounded-full h-10 w-10 p-0 shrink-0"
              >
                {sending ? <Loader2 className="w-4 h-4 cs-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
